import { useTranslation } from 'react-i18next';

import PropTypes from 'prop-types';

import { useMutation, useQuery } from 'react-query';
import { useCallback, useMemo, useRef } from 'react';
import { Tooltip } from 'primereact/tooltip';
import { downloadFile } from 'apis/proposals.api';
import MultipleSelect from 'components/FormControl/MultipleSelect';
import { getAllOrganizations } from 'apis/projectProposals.api';
import FileInput from 'components/FormControl/FileInput';
import { TextareaInput, Select, HugerteEditor, TextInput } from 'components/FormControl';
import { getProjectTypes } from 'utils/func';
import AddOwnerDialog from '../AddOwnerDialog';

export default function ProposalForm({
  disabled,
  researchFields,
  control,
  errors,
  setValue,
  watch,
  proposalDetailData,
}) {
  const { t } = useTranslation();
  const researcherRef = useRef();
  const owner = watch('owner');

  const { mutate: mutateDownloadFile } = useMutation(downloadFile);

  const handleDownloadFile = (path) => {
    mutateDownloadFile(
      { path, id: proposalDetailData?.id },
      {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            // eslint-disable-next-line prefer-destructuring
            link.download = path.split('__')[1];

            link.click();
          }
        },
      }
    );
  };

  const { data: organizationsData } = useQuery(
    ['projectProposalOrganizations'],
    getAllOrganizations,
    { staleTime: 1000 * 60 * 5 }
  );

  const organizations = useMemo(
    () =>
      organizationsData?.data.map((e) => ({
        ...e,
        name: t('lang') === 'vi' ? e?.name : e?.englishName,
      })) ?? [],
    [organizationsData, t]
  );

  const projectTypes = getProjectTypes(t, true);

  const handleOpenOwnerModal = useCallback(() => {
    researcherRef.current.open({
      type: owner ? 'UPDATE' : 'CREATE',
      updateOwner: setValue,
      value: owner,
    });
  }, [researcherRef.current, owner, setValue]);

  const yearsProposed = useMemo(() => {
    const years = [];
    if (proposalDetailData?.yearProposed) {
      years.push({
        value: proposalDetailData?.yearProposed,
        name: proposalDetailData?.yearProposed?.toString(),
      });
    } else {
      const currentYear = new Date().getFullYear();
      years.push({ value: currentYear, name: currentYear.toString() });
    }
    return years;
  }, [proposalDetailData]);

  const typeProposalOptions = [
    { name: t('proposal.typeProposal.topic'), value: 'topic' },
    { name: t('proposal.typeProposal.project'), value: 'project' },
    { name: t('proposal.typeProposal.productionProject'), value: 'production_project' },
  ];

  // #endregion Data

  // #region Event
  // #endregion Event

  return (
    <div className="relative">
      <h4 className="text-center mb-4">{t('proposal.create.label')}</h4>
      <div className="grid p-fluid">
        <div className="col-12">
          <TextareaInput
            label={t('proposal.create.name')}
            name="vietnameseName"
            isRequired
            readOnly={disabled}
            control={control}
            errors={errors}
          />
        </div>
        <div className="col-6">
          <MultipleSelect
            label={t('proposal.create.researchField')}
            name="researchFieldIds"
            options={researchFields}
            inputField="id"
            renderField="name"
            isRequired
            control={control}
            errors={errors}
            readOnly={disabled}
          />
        </div>

        <div className="grid col-12">
          <div className="col-3">
            <Select
              label={t('proposal.create.yearProposed')}
              name="yearProposed"
              isRequired
              options={yearsProposed}
              inputField="value"
              // disabled={disabled}
              control={control}
              errors={errors}
              readOnly={disabled}
            />
          </div>
          <div className="col-3">
            <Select
              label={t('proposal.create.typeProposal')}
              name="type"
              isRequired
              options={typeProposalOptions}
              inputField="value"
              control={control}
              errors={errors}
              readOnly={disabled}
            />
          </div>
          <div className="col-3">
            <Select
              label={t('projectProposal.general.projectNameAndType.projectType')}
              name="projectType"
              options={projectTypes}
              inputField="value"
              readOnly={disabled}
              control={control}
              errors={errors}
            />
          </div>
        </div>

        <div className="col-3">
          <div className="mb-1">
            <span>{t('projectProposal.general.researchHumanResources.ownerFields.label')}</span>
            {!disabled && (
              <>
                <i
                  onClick={handleOpenOwnerModal}
                  className={`pi pi-${
                    owner?.fullname ? 'pencil' : 'plus'
                  } text-primary cursor-pointer bg-primary p-1 border-circle ml-1`}
                  data-pr-tooltip={t('formLayout.action.add')}
                  data-pr-position="left"
                />
                <Tooltip target=".add-owner" />
              </>
            )}
          </div>
          <TextInput name="owner.fullname" isRequired control={control} errors={errors} readOnly />
        </div>
        <div className="col-12">
          <HugerteEditor
            label={t('proposal.create.target')}
            name="target"
            isRequired
            control={control}
            errors={errors}
            readOnly={disabled}
          />
        </div>

        <div className="col-12">
          <h5>
            {t('proposal.create.uploadFiles')}
            <span className="text-red-500 text-lg ml-1">*</span>
            <span className="text-red-500 text-lg ml-1">
              ({t('proposal.create.uploadFileNote')})
            </span>
          </h5>
          <ul>
            <li style={{ marginBottom: '8px' }}>
              {t('proposal.R01DA')}:{' '}
              <a href="/files/R01.[SEO].ĐX.ĐA.docx">
                {t('formLayout.action.download')} <i className="pi pi-download ml-1" />
              </a>
            </li>
            <li style={{ marginBottom: '8px' }}>
              {t('proposal.R02DT')}:{' '}
              <a href="/files/R01.[SEO].ĐX.ĐT.docx">
                {t('formLayout.action.download')} <i className="pi pi-download ml-1" />
              </a>
            </li>
            <li>
              {t('proposal.R03SXTN')}:{' '}
              <a href="/files/R01.[SEO].ĐX.SXTN.docx">
                {t('formLayout.action.download')} <i className="pi pi-download ml-1" />
              </a>
            </li>
          </ul>
        </div>
        <div className="col-12">
          {!disabled && (
            <FileInput
              name="file"
              accept=".pdf"
              control={control}
              errors={errors}
              acceptOnly="pdfOnly"
            />
          )}

          {proposalDetailData?.attachmentFileUrl && (
            <span
              className="p-link text-primary underline"
              onClick={
                () => handleDownloadFile(proposalDetailData.attachmentFileUrl)
                // eslint-disable-next-line react/jsx-curly-newline
              }
            >
              {proposalDetailData.attachmentFileUrl.split('__')[1]}
            </span>
          )}
        </div>
      </div>
      <AddOwnerDialog ref={researcherRef} organizations={organizations} />
    </div>
  );
}

ProposalForm.propTypes = {
  disabled: PropTypes.bool,
  researchFields: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  control: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({}).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  proposalDetailData: PropTypes.object,
  // eslint-disable-next-line react/require-default-props
  setValue: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  watch: PropTypes.func,
};

ProposalForm.defaultProps = {
  disabled: false,
  proposalDetailData: {},
};
